import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Package, ChevronRight, LogOut, ShoppingBag, Clock, Gift } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { api } from '../services/api';

const STATUS_COLORS = {
  delivered: 'bg-green-100 text-green-700',
  confirmed: 'bg-blue-100 text-blue-700',
  preparing: 'bg-amber-100 text-amber-700',
  cancelled: 'bg-red-100 text-red-700',
  pending: 'bg-gray-100 text-gray-700',
  out_for_delivery: 'bg-purple-100 text-purple-700',
  completed: 'bg-emerald-100 text-emerald-700',
  ready: 'bg-teal-100 text-teal-700',
  expired: 'bg-gray-200 text-gray-500',
  rejected: 'bg-red-100 text-red-700'
};

function DeliveryIcon({ className = "w-3 h-3" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
    >
      <path fillRule="evenodd" clipRule="evenodd" d="M19.8438 15.0794L18.3009 10.0302L16.1882 10.6882L17.662 15.5113C16.1796 16.2322 15.1565 17.7633 15.1565 19.5357C15.1565 22.0012 17.1363 24 19.5783 24C22.0203 24 24 22.0012 24 19.5357C24 17.1602 22.1622 15.218 19.8438 15.0794ZM19.5783 21.7678C20.7993 21.7678 21.7891 20.7685 21.7891 19.5357C21.7891 18.3029 20.7993 17.3036 19.5783 17.3036C18.3572 17.3036 17.3674 18.3029 17.3674 19.5357C17.3674 20.7685 18.3572 21.7678 19.5783 21.7678Z"></path>
      <path fillRule="evenodd" clipRule="evenodd" d="M6.31306 23.9998C8.75511 23.9998 10.7348 22.001 10.7348 19.5355C10.7348 17.07 8.75511 15.0713 6.31306 15.0713C3.87101 15.0713 1.89133 17.07 1.89133 19.5355C1.89133 22.001 3.87101 23.9998 6.31306 23.9998ZM6.31306 21.7676C7.53409 21.7676 8.52392 20.7683 8.52392 19.5355C8.52392 18.3027 7.53409 17.3034 6.31306 17.3034C5.09203 17.3034 4.1022 18.3027 4.1022 19.5355C4.1022 20.7683 5.09203 21.7676 6.31306 21.7676Z"></path>
      <path fillRule="evenodd" clipRule="evenodd" d="M15.1565 6.14274C16.3776 6.14274 17.3674 5.14338 17.3674 3.91061C17.3674 2.67784 16.3776 1.67848 15.1565 1.67848C13.9355 1.67848 12.9457 2.67784 12.9457 3.91061C12.9457 5.14338 13.9355 6.14274 15.1565 6.14274Z"></path>
      <path fillRule="evenodd" clipRule="evenodd" d="M11.3107 7.53533C12.134 6.49648 13.6687 6.41066 14.6005 7.35137L16.7197 9.49095H19.5781C20.1887 9.49095 20.6836 9.99064 20.6836 10.607C20.6836 11.2234 20.1887 11.7231 19.5781 11.7231H16.5162C16.0601 11.7231 15.6228 11.5402 15.3002 11.2146L13.0372 8.92973L10.1402 12.5857L13.7513 15.0163C14.356 15.4233 14.642 16.1701 14.4658 16.8819L13.4652 20.9223C13.3172 21.5203 12.717 21.8839 12.1247 21.7343C11.5324 21.5849 11.1723 20.9789 11.3204 20.3809L12.2369 16.6797L8.91384 14.443C7.835 13.7169 7.60383 12.2136 8.41381 11.1914L11.3107 7.53533Z"></path>
      <rect width="7.21058" height="8.74543" rx="2" transform="matrix(0.763004 0.646394 -0.639173 0.769063 5.58984 1.52588e-05)"></rect>
    </svg>
  );
}

function PickupIcon({ className = "w-3 h-3" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
    >
      <path d="M13.099 10.6562C12.8875 11.5812 12.7048 12.4267 12.4933 13.2665C12.4148 13.5816 12.519 13.8099 12.6897 14.0657C14.1622 16.2713 15.6212 18.4855 17.0846 20.6954C17.3006 21.0206 17.5483 21.3444 17.5287 21.7476C17.5015 22.3344 17.1813 22.7622 16.5893 22.9256C15.964 23.0975 15.4505 22.8793 15.0986 22.3547C14.2468 21.0799 13.4025 19.8008 12.5568 18.5231C11.4573 16.8595 10.3684 15.1888 9.25226 13.5339C9.03025 13.2043 8.9789 12.9225 9.07556 12.5323C9.35949 11.3962 9.57697 10.2458 9.85487 8.93919C8.85052 9.67775 7.88998 10.2082 7.84165 11.4555C7.81446 12.1492 7.68609 12.8416 7.58792 13.531C7.48673 14.2392 7.04723 14.628 6.47785 14.5398C5.8979 14.4502 5.59584 13.9675 5.68797 13.2737C5.82843 12.2244 5.97342 11.1765 6.10783 10.1272C6.17126 9.62716 6.39026 9.21235 6.79501 8.88716C7.62265 8.22231 8.43821 7.54156 9.28096 6.8955C10.0421 6.3116 10.9106 5.95605 11.9028 5.93726C12.7229 5.9228 13.3542 6.21765 13.7529 6.94031C14.1652 7.68754 14.5066 8.46223 14.7044 9.28028C14.9279 10.2053 15.4641 10.8065 16.3899 11.1462C17.0423 11.3861 17.6646 11.7055 18.2959 11.9989C18.9272 12.2923 19.1688 12.8011 18.8774 13.2708C18.5421 13.8085 18.0588 13.8258 17.5105 13.5671C16.8143 13.2376 16.1105 12.9225 15.4082 12.6045C13.9372 11.9397 13.9372 11.9397 13.099 10.6562Z"></path>
      <path d="M10.2077 16.9911C10.5762 17.7079 10.1352 18.4725 9.74406 19.0636C8.95417 20.256 8.02232 21.366 7.12521 22.4905C6.67212 23.0585 5.99702 23.1496 5.47295 22.7738C4.94586 22.3951 4.83409 21.7404 5.26604 21.1796C6.7129 19.3007 8.41047 17.5547 8.51921 14.9936"></path>
      <path d="M13.1715 1.00004C14.4311 0.992815 15.4158 1.93805 15.4007 3.13911C15.3871 4.25924 14.4175 5.18858 13.244 5.21026C12.001 5.23338 10.9649 4.2708 10.971 3.10009C10.9755 1.94239 11.9557 1.00727 13.1715 1.00004Z"></path>
    </svg>
  );
}

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, logout, setUser } = useApp();
  const [restaurant, setRestaurant] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
  api.getRestaurant()
    .then((rest) => {
      setRestaurant(rest);
    })
    .catch(() => {});
}, []);

useEffect(() => {
  const checkSession = async () => {
    const token = localStorage.getItem('mj_token');
    if (!token) return;

    try {
      const me = await api.getMe();
      setUser(me);
    } catch (err) {
      logout();
      localStorage.removeItem('mj_token');
      navigate('/login');
    }
  };

  checkSession();
}, []);

  useEffect(() => {
    if (user && activeTab === 'orders') {
      setLoading(true);
      api.getOrders()
        .then(data => setOrders(data.filter(order => order.status !== 'pending')))
        .catch((err) => {
            if (err?.response?.status === 401) {
              logout();
              localStorage.removeItem('mj_token');
              navigate('/login');
            } else {
              setOrders([]);
            }
          })
        .finally(() => setLoading(false));
    }
  }, [user, activeTab]);

  useEffect(() => {
    if (user && editMode) {
      setEditForm({ name: user.name || '', phone: user.phone || '' });
    }
  }, [user, editMode]);

  useEffect(() => {
  if (restaurant?.name) {
    document.title = `My Account - ${restaurant.name}`;
  } else {
    document.title = "My Account";
  }
}, [restaurant]);

  const handleSaveProfile = async () => {
    setSaving(true);
    setSaveSuccess(false);
    try {
      const updated = await api.updateProfile(editForm);
      setUser({ ...user, name: updated.name, phone: updated.phone });
      setSaveSuccess(true);
      setTimeout(() => {
        setEditMode(false);
        setSaveSuccess(false);
      }, 1500);
    } catch (err) {
      alert('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Sign in to your account</h2>
          <p className="text-gray-500 mb-6 text-sm">View your orders and manage your profile</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-gray-900 text-white px-8 py-3 rounded-xl font-semibold hover:bg-amber-700 transition-colors duration-200 w-full"
          >
            Sign In
          </button>

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="px-3 text-xs text-gray-400 font-medium">or</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Back to Menu */}
          <button
            onClick={() => navigate('/')}
            className="w-full border border-gray-300 text-gray-700 px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors duration-200"
          >
            Back to Menu
          </button>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    localStorage.removeItem('mj_token');
    navigate('/');
  };

  return (
      <div className="min-h-screen bg-gray-50 font-sans">
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h1 className="text-lg font-bold text-gray-900">My Account</h1>
          <button onClick={handleLogout} className="ml-auto flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-600 transition-colors">
            <LogOut className="w-4 h-4" /><span>Sign out</span>
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-amber-700 rounded-full flex items-center justify-center text-white font-bold text-xl">
              {user.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{user.name}</h2>
              <p className="text-gray-500 text-sm">{user.email}</p>
              {user.isAdmin && (
                <span className="inline-block text-xs bg-amber-100 text-amber-700 font-semibold px-2 py-0.5 rounded-md mt-1">Admin</span>
              )}
            </div>
            {user.isAdmin && (
              <button onClick={() => navigate('/admin')} className="ml-auto bg-amber-700 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-amber-800 transition-colors">
                Admin Panel
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="grid grid-cols-2 gap-1 bg-gray-100 p-1 rounded-xl">
          {[{ id: 'orders', label: 'Past Orders', icon: Package }, { id: 'profile', label: 'Profile', icon: User }].map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-1.5 ${activeTab === tab.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
                <Icon className="w-4 h-4" />{tab.label}
              </button>
            );
          })}
        </div>

        {/* Orders */}
        {activeTab === 'orders' && (
          <div className="space-y-3">
            {loading ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <div className="w-8 h-8 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-2" style={{ borderWidth: 2 }} />
                <p className="text-gray-400 text-sm">Loading orders...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <ShoppingBag className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500 font-medium">No orders yet</p>
                <p className="text-gray-400 text-sm mt-1">Your past orders will appear here</p>
                <button onClick={() => navigate('/')} className="mt-4 bg-gray-900 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors">
                  Start Ordering
                </button>
              </div>
            ) : (
              orders.map(order => {
                const isExpiredOrRejected = order.status === 'expired' || order.status === 'rejected';

                const orderSubtotal = order.subtotal || order.total || 0;

                const discountAmount = order.discount || 0;

                const pointsBase = Math.max(orderSubtotal - discountAmount, 0);

                const earnedPoints = restaurant?.pointsPerPound
                  ? Math.floor(pointsBase * restaurant.pointsPerPound)
                  : 0;
                return (
                  <div 
                    key={order.id} 
                    className={`bg-white rounded-2xl border border-gray-200 p-4 transition-colors ${
                      isExpiredOrRejected ? 'opacity-75 cursor-default' : 'hover:border-amber-200 cursor-pointer'
                    }`}
                    onClick={() => !isExpiredOrRejected && navigate(`/order-status/${order.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">Order #{order.id.slice(-8).toUpperCase()}</p>
                        <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(order.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS_COLORS[order.status] || STATUS_COLORS.pending}`}>
                          {order.status?.replace('_', ' ')}
                        </span>
                        <span
                            className="text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 bg-slate-700 text-white"
                          >
                            {order.type === 'delivery' ? (
                              <>
                                <DeliveryIcon className="w-3.5 h-3.5" />
                                Delivery
                              </>
                            ) : (
                              <>
                                <PickupIcon className="w-3.5 h-3.5" />
                                Pickup
                              </>
                            )}
                          </span>
                       </div>
                    </div>
                    {isExpiredOrRejected && (
                      <div className="mb-3 bg-gray-50 border border-gray-200 rounded-lg p-2">
                        <p className="text-xs text-gray-600">
                          {order.status === 'expired' 
                            ? 'This order was not confirmed in time and has expired.'
                            : 'This order was rejected by the restaurant.'}
                        </p>
                      </div>
                    )}
                    <div className="space-y-1 mb-3">
                      {order.items?.slice(0, 3).map((item, i) => (
  <div key={i} className="text-sm text-gray-600">

    <p>
      {item.quantity}x {item.name}
      <span className="text-gray-400 ml-1">
        · £{(item.price * item.quantity).toFixed(2)}
      </span>
    </p>

    {item.selectedOptions &&
      Object.keys(item.selectedOptions).length > 0 && (
        <div className="mt-1 ml-3 space-y-0.5">
          {Object.values(item.selectedOptions).map((choice, idx) => {

            // multiple selections
            if (Array.isArray(choice)) {
              return choice.map((c, subIdx) => (
                <p
                  key={`${idx}-${subIdx}`}
                  className="text-xs text-gray-400"
                >
                  + {c.name}
                </p>
              ));
            }

            // single selection
            return (
              <p
                key={idx}
                className="text-xs text-gray-400"
              >
                + {choice?.name}
              </p>
            );
          })}
        </div>
      )}

  </div>
))}
                      {order.items?.length > 3 && <p className="text-xs text-gray-400">+{order.items.length - 3} more items</p>}
                    </div>
                    <div className="border-t border-gray-100 pt-3 space-y-1">
                      <div className="flex justify-between text-sm text-gray-600">
                        <span>Subtotal:</span>
                        <span>£{(order.subtotal || order.total || 0).toFixed(2)}</span>
                      </div>
                      {order.deliveryFee > 0 && (
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Delivery Fee:</span>
                          <span>£{order.deliveryFee.toFixed(2)}</span>
                        </div>
                      )}
                      {order.serviceCharge > 0 && (
                        <div className="flex justify-between text-sm text-gray-600">
                          <span>Service Charge:</span>
                          <span>£{order.serviceCharge.toFixed(2)}</span>
                        </div>
                      )}

                          {discountAmount > 0 && (
                            <div className="flex justify-between text-sm text-green-700">
                              <span>
                                Discount
                                {order.couponCode ? ` (${order.couponCode})` : ''}
                              </span>

                              <span className="font-semibold">
                                -£{discountAmount.toFixed(2)}
                              </span>
                            </div>
                          )}
                          {restaurant?.rewardsEnabled && earnedPoints > 0 && (
                            <div className="flex justify-between text-sm text-amber-700">
                              <span className="flex items-center gap-1.5 font-medium">
                                <Gift className="w-3.5 h-3.5" />
                                Earned Points
                              </span>

                              <span className="font-semibold">
                                +{earnedPoints} pts
                              </span>
                            </div>
                          )}

                      <div className="flex justify-between text-sm font-bold text-gray-900 pt-1 border-t border-gray-100">
                        <span>Total:</span>
                        <span>£{(order.total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Profile */}
        {activeTab === 'profile' && (
          <div className="space-y-3">
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Personal Information</h3>
                {!editMode && (
                  <button 
                    onClick={() => setEditMode(true)}
                    data-testid="edit-profile-button"
                    className="text-sm text-amber-700 font-semibold hover:text-amber-800"
                  >
                    Edit
                  </button>
                )}
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Full Name</label>
                  {editMode ? (
                    <input 
                      type="text" 
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      data-testid="profile-name-input"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" 
                    />
                  ) : (
                    <p className="text-sm text-gray-900 py-3">{user.name}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Email Address</label>
                  <p className="text-sm text-gray-900 py-3">{user.email}</p>
                  {editMode && <p className="text-xs text-gray-400">Email cannot be changed</p>}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Phone Number</label>
                  {editMode ? (
                    <input 
                      type="tel" 
                      value={editForm.phone}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      placeholder="+44 XXXX XXXXXX"
                      data-testid="profile-phone-input"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 transition-colors" 
                    />
                  ) : (
                    <p className="text-sm text-gray-900 py-3">{user.phone || 'Not set'}</p>
                  )}
                </div>
              </div>
            </div>
            {editMode ? (
              <div className="flex gap-3">
                <button 
                  onClick={() => setEditMode(false)}
                  disabled={saving}
                  className="flex-1 border-2 border-gray-200 text-gray-700 py-3.5 rounded-xl font-semibold hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSaveProfile}
                  disabled={saving}
                  data-testid="save-profile-button"
                  className="flex-1 bg-gray-900 text-white py-3.5 rounded-xl font-semibold hover:bg-amber-700 transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : saveSuccess ? (
                    <>Saved!</>
                  ) : (
                    <>Save Changes</>
                  )}
                </button>
              </div>
            ) : (
              <button onClick={handleLogout}
                className="w-full border-2 border-red-200 text-red-600 py-3.5 rounded-xl font-semibold hover:bg-red-50 transition-colors duration-200 flex items-center justify-center gap-2">
                <LogOut className="w-4 h-4" />Sign Out
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
